(() => {
  'use strict';
  const iso = date => date.toISOString().slice(0, 10);
  const day = value => new Date(value + 'T12:00:00Z');
  const shift = (date, days) => { const result = new Date(date); result.setUTCDate(result.getUTCDate() + days); return result; };
  const easter = year => {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    return new Date(Date.UTC(year, Math.floor((h + l - 7 * m + 114) / 31) - 1, (h + l - 7 * m + 114) % 31 + 1, 12));
  };
  const holidays = (year, country) => {
    const dates = new Set([year + '-01-01', year + '-12-25']);
    const pasen = easter(year);
    [1, 39, 50].forEach(offset => dates.add(iso(shift(pasen, offset))));
    if (country === 'NL') {
      let kingsDay = day(year + '-04-27');
      if (kingsDay.getUTCDay() === 0) kingsDay = shift(kingsDay, -1);
      dates.add(iso(kingsDay)); dates.add(year + '-12-26');
      if (year % 5 === 0) dates.add(year + '-05-05');
    }
    if (country === 'BE') ['05-01', '07-21', '08-15', '11-01', '11-11'].forEach(value => dates.add(year + '-' + value));
    return dates;
  };
  const calendar = (country, extra = '') => {
    const closed = new Set(extra.split(/[\s,;]+/).filter(value => /^\d{4}-\d{2}-\d{2}$/.test(value)));
    const cache = new Map();
    const holiday = (date, place) => {
      const key = date.getUTCFullYear() + place;
      if (!cache.has(key)) cache.set(key, holidays(date.getUTCFullYear(), place));
      return closed.has(iso(date)) || cache.get(key).has(iso(date));
    };
    return {
      work: date => date.getUTCDay() > 0 && date.getUTCDay() < 6 && !holiday(date, 'NL') && !holiday(date, country),
      ship: date => date.getUTCDay() > 0 && date.getUTCDay() < 6 && !holiday(date, 'NL'),
      deliver: date => date.getUTCDay() > 1 && !holiday(date, 'NL') && !holiday(date, country)
    };
  };
  const advance = (date, count, allowed) => {
    let result = new Date(date), remaining = count, attempts = 0;
    while (remaining > 0) {
      if (++attempts > 370) throw new Error('delivery_calendar');
      result = shift(result, 1);
      if (allowed(result)) remaining--;
    }
    return result;
  };
  const nextAllowed = (date, allowed) => {
    let result = new Date(date), attempts = 0;
    while (!allowed(result)) { if (++attempts > 370) throw new Error('delivery_calendar'); result = shift(result, 1); }
    return result;
  };
  const local = instant => {
    const parts = new Intl.DateTimeFormat('en-GB', {timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).formatToParts(instant);
    const part = name => parts.find(value => value.type === name).value;
    return {date: day(part('year') + '-' + part('month') + '-' + part('day')), hour: Number(part('hour')), minute: Number(part('minute'))};
  };
  const supplierPlan = (plan, settings, instant = new Date()) => {
    if (!plan || !Array.isArray(plan.orderDays)) return null;
    const cutoff = /^(\d{1,2}):(\d{2})$/.exec(String(plan.cutoff || '').trim());
    if (!cutoff || Number(cutoff[1]) > 23 || Number(cutoff[2]) > 59) return null;
    const orderDays = new Set(plan.orderDays.filter(value => Number.isInteger(value) && value >= 1 && value <= 5));
    if (!orderDays.size || !Number.isInteger(plan.leadMin) || !Number.isInteger(plan.leadMax) || plan.leadMin < 1 || plan.leadMax < plan.leadMin || plan.leadMax > 30 || !Number.isInteger(plan.processing) || plan.processing < 0 || plan.processing > 5) return null;
    const current = local(instant), cal = calendar('BE', settings.closed || ''), warehouse = calendar('NL', settings.closed || '');
    const purchaseDay = date => warehouse.work(date) && orderDays.has(date.getUTCDay());
    const cutoffMinutes = Number(cutoff[1]) * 60 + Number(cutoff[2]);
    const purchase = nextAllowed(current.hour * 60 + current.minute >= cutoffMinutes ? shift(current.date, 1) : current.date, purchaseDay);
    const receiveFrom = advance(purchase, plan.leadMin, warehouse.work), receiveTo = advance(purchase, plan.leadMax, warehouse.work);
    const dispatchFrom = nextAllowed(advance(receiveFrom, plan.processing, warehouse.work), warehouse.ship), dispatchTo = nextAllowed(advance(receiveTo, plan.processing, warehouse.work), warehouse.ship);
    return {orderDate: iso(purchase), receiveFrom: iso(receiveFrom), receiveTo: iso(receiveTo), dispatchFrom: iso(dispatchFrom), dispatchTo: iso(dispatchTo), from: iso(advance(dispatchFrom, 1, cal.deliver)), to: iso(advance(dispatchTo, 1, cal.deliver))};
  };
  const ownPlan = (settings, instant = new Date()) => {
    settings = settings.planning || settings;
    const current = local(instant), cal = calendar('BE', settings.closed || '');
    const cutoff = Number(settings.cutoff ?? 17);
    if (!Number.isFinite(cutoff) || cutoff < 0 || cutoff > 24) return null;
    const dispatch = nextAllowed(current.hour * 60 + current.minute >= cutoff * 60 ? shift(current.date, 1) : current.date, cal.ship);
    const arrival = iso(advance(dispatch, 1, cal.deliver));
    return {dispatch: iso(dispatch), from: arrival, to: arrival};
  };
  const calculate = (items, settings, instant = new Date()) => {
    settings = settings.planning || settings;
    const current = local(instant), country = 'NL_BE', cal = calendar('BE', settings.closed || '');
    const own = ownPlan(settings, instant);
    const grouped = new Map();
    for (const item of items) {
      if (!item.shipping) continue;
      const previous = grouped.get(item.id);
      if (previous) previous.quantity += item.quantity;
      else grouped.set(item.id, {...item});
    }
    const lines = [...grouped.values()].map(item => {
      const result = {id: item.id, sku: item.sku || '', title: item.title, quantity: item.quantity, own: Math.max(0, item.own), supplier: 0, source: 'unknown', from: '', to: ''};
      if (!item.available || !item.tracked || item.preorder) return result;
      if (item.quantity <= item.own) {
        return own ? {...result, source: 'own', from: own.from, to: own.to} : result;
      }
      const fresh = item.linked && item.status === 'ok' && item.unit === 'shop_unit' && item.updated > 0 && item.updated * 1000 <= instant.getTime() && item.expires * 1000 > instant.getTime();
      if (!fresh || !item.continue || item.supplier <= 0 || item.quantity > item.own + item.supplier) return result;
      const plan = supplierPlan(settings.suppliers?.[item.supplierKey || 'default'], settings, instant);
      if (!plan) return result;
      return {...result, source: 'supplier', supplier: item.quantity - Math.max(0, item.own), ...plan};
    });
    const complete = lines.length > 0 && lines.every(line => line.from && line.to);
    return {country, today: iso(current.date), nextWorkingDay: iso(advance(current.date, 1, cal.work)), at: instant.toISOString(), lines, complete, from: complete ? lines.map(line => line.from).sort().at(-1) : '', to: complete ? lines.map(line => line.to).sort().at(-1) : ''};
  };
  const dateRules = model => {
    const required = model.lines.some(line => line.source !== 'own');
    const earliestKnown = model.lines.map(line => line.from).filter(Boolean).sort().at(-1);
    return {required, min: model.complete ? model.from : earliestKnown || model.today};
  };
  const signature = model => JSON.stringify([model.country, model.from, model.to, model.lines]);
  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined') module.exports = {calculate, ownPlan, supplierPlan, holidays, local, signature, dateRules};
    return;
  }
  if (window.FXLDelivery?.supplierEstimate) return;
  const clocks = new WeakMap();
  const dateLabel = (value, locale) => new Intl.DateTimeFormat(locale || 'nl-NL', {timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long'}).format(day(value));
  const translate = (strings, key, values = {}) => { let value = strings[key] || ''; for (const [name, text] of Object.entries(values)) value = value.replaceAll('[' + name + ']', text); return value; };
  const interval = (line, config) => !line.from ? config.strings.unknown_item : line.from === line.to ? translate(config.strings, 'on', {date: dateLabel(line.from, config.locale)}) : translate(config.strings, 'between', {from: dateLabel(line.from, config.locale), to: dateLabel(line.to, config.locale)});
  const refresh = root => {
    const element = root.querySelector('[data-fxl-delivery]');
    if (!element) return null;
    const config = JSON.parse(element.querySelector('[data-fxl-delivery-config]').textContent);
    if (!clocks.has(element)) clocks.set(element, {server: new Date(config.now).getTime(), started: performance.now()});
    const clock = clocks.get(element), instant = new Date(clock.server + performance.now() - clock.started);
    const country = 'NL_BE';
    const model = calculate(config.items, {...config, country}, instant);
    const summary = model.complete ? interval(model, config) : model.lines.length ? config.strings.unknown : config.strings.not_shipping;
    const displayRange = model.complete ? model.from === model.to ? dateLabel(model.from, config.locale) : translate(config.strings, 'range_only', {from: dateLabel(model.from, config.locale), to: dateLabel(model.to, config.locale)}) : summary;
    element.querySelector('[data-fxl-delivery-summary]').textContent = displayRange;
    const selectedDate = root.querySelector('[data-fxlcp-date]')?.value;
    const rules = dateRules(model), input = root.querySelector('[data-fxlcp-date]');
    if (input) {
      input.disabled = false;
      input.required = rules.required;
      input.setAttribute('aria-required', String(rules.required));
      input.min = rules.min;
      const block = input.closest('[data-fxlcp-date-block]');
      block.classList.toggle('fxlcp__date--required', rules.required);
      block.dataset.required = String(rules.required);
      block.querySelector('[data-fxlcp-date-badge]').textContent = config.strings[rules.required ? 'required' : 'optional'];
      block.querySelector('[data-fxlcp-date-hint]').textContent = config.strings[rules.required ? 'required_intro' : 'optional_intro'];
      block.querySelector('[data-fxlcp-date-note]').hidden = !rules.required;
    }
    let warning = '';
    if (selectedDate && model.lines.length) {
      if (!model.complete) warning = config.strings.unsure;
      else if (selectedDate < model.from) warning = config.strings.too_soon;
      else if (selectedDate <= model.to) warning = config.strings.tight;
    }
    const warningNode = element.querySelector('[data-fxl-delivery-warning]');
    warningNode.textContent = warning; warningNode.hidden = !warning;
    const attributes = {levering_land: ''};
    for (const key of Object.keys(config.attributes || {})) if (/^(Verwachte levering|Leverindicatie |Levering artikel )/.test(key)) attributes[key] = '';
    Object.assign(attributes, {'Verwachte levering': summary, 'Verwachte levering vanaf': model.from, 'Verwachte levering t/m': model.to});
    const fields = element.querySelector('[data-fxl-delivery-attributes]'), form = root.querySelector('[data-fxlcp-checkout-form]');
    fields.replaceChildren(...Object.entries(attributes).map(([key, value]) => {
      const input = document.createElement('input'); input.type = 'hidden'; input.name = 'attributes[' + key + ']'; input.value = value;
      if (form) input.setAttribute('form', form.id); return input;
    }));
    return {model, rules, attributes, signature: signature(model)};
  };
  let globalConfig, globalClock, cacheMinute;
  const estimates = new Map();
  const estimate = (key, makePlan) => {
    const node = document.querySelector('[data-fxl-delivery-global]');
    if (!node) return null;
    if (!globalConfig) {
      try { globalConfig = JSON.parse(node.textContent); } catch { return null; }
      globalClock = {server: new Date(globalConfig.now).getTime(), started: performance.now()};
    }
    const instant = new Date(globalClock.server + performance.now() - globalClock.started), minute = Math.floor(instant.getTime() / 60000);
    if (!Number.isFinite(minute)) return null;
    if (cacheMinute !== minute) { estimates.clear(); cacheMinute = minute; }
    if (!estimates.has(key)) {
      const plan = makePlan(globalConfig.planning || {}, instant);
      const format = new Intl.DateTimeFormat(globalConfig.locale || 'nl-NL', {timeZone:'UTC',weekday:'long',day:'numeric',month:'short'});
      const label = plan ? plan.from === plan.to ? format.format(day(plan.from)) : format.format(day(plan.from)) + ' – ' + format.format(day(plan.to)) : globalConfig.unknown;
      estimates.set(key, {plan, label});
    }
    return estimates.get(key);
  };
  const supplierEstimate = key => estimate('supplier:' + key, (settings, instant) => supplierPlan(settings.suppliers?.[key], settings, instant));
  const ownEstimate = () => estimate('own', ownPlan);
  window.FXLDelivery = {refresh, signature, supplierEstimate, ownEstimate};
  if (!customElements.get('fxl-own-delivery')) {
    const active = new Set();
    let timer;
    customElements.define('fxl-own-delivery', class extends HTMLElement {
      connectedCallback() {
        this.refresh = () => {
          const delivery = ownEstimate();
          this.hidden = !delivery?.plan;
          if (!delivery?.plan) return;
          const text = (this.dataset.template || '').replaceAll('[delivery]', delivery.label);
          if (this.textContent !== text) this.textContent = text;
        };
        active.add(this);
        this.refresh();
        if (!timer) timer = setInterval(() => active.forEach(element => element.refresh()), 30000);
      }
      disconnectedCallback() {
        active.delete(this);
        if (!active.size) { clearInterval(timer); timer = null; }
      }
    });
  }
  document.dispatchEvent(new CustomEvent('fxl:delivery-ready'));
})();
